import React, { useEffect } from 'react';
import { Text } from '../../../../../CommonComponents/OPL/Text/Text';
import { Box } from '../../../../../CommonComponents/OPL/Box/Box';
import { ClientAccessSettingsProps } from '../../../../ClientAccess.types';
import { Toggle } from '../../../../../CommonComponents/OPL/Toggle/Toggle';
import { Separator } from '../../../../../CommonComponents/OPL/Separator/Separator';
import { DashboardFeatureId } from '../../../../../AdviserArea/AdviserArea.types';

export const Other: React.FC<ClientAccessSettingsProps> = ({
  linkAccountsAccess,
  dataReconciliationAccess,
  isProBonoClient,
  showDataAggregation,
  showDataReconciliation,
  showProBono,
  canAddManualAccount,
  displayAddManualAccountToggle,
  allowMobileAppPushNotifications,
  defaultDocumentUploadMobilePushNotifications,
  defaultNewsfeedMobilePushNotifications,
  displayMobileAppPushNotificationsSection,
  onChange,
  hasDocumentVaultFirm,
  isNewsfeedEnabledOnOC,
  dashboardAccess,
}) => {
  const hasOtherSettings =
    showDataAggregation || displayAddManualAccountToggle || showDataReconciliation || showProBono;
  if (!hasOtherSettings && !displayMobileAppPushNotificationsSection) {
    return null;
  }

  const hasDocumentVaultAccess = dashboardAccess?.includes(DashboardFeatureId.DocumentVault);

  const handleMobileAppPushNotificationsChange = (checked: boolean) => {
    onChange({
      allowMobileAppPushNotifications: checked,
      ...(hasDocumentVaultFirm && {
        defaultDocumentUploadMobilePushNotifications: checked && hasDocumentVaultAccess,
      }),
      ...(isNewsfeedEnabledOnOC && {
        defaultNewsfeedMobilePushNotifications: checked,
      }),
    });
  };

  useEffect(() => {
    if (!displayMobileAppPushNotificationsSection || hasDocumentVaultAccess) {
      return;
    }

    if (isNewsfeedEnabledOnOC) {
      // Only push an update when there is actually something to reset, otherwise
      // the cache mutation returns a new object every render and loops forever.
      if (defaultDocumentUploadMobilePushNotifications) {
        onChange({ defaultDocumentUploadMobilePushNotifications: false });
      }
    } else if (
      allowMobileAppPushNotifications ||
      defaultDocumentUploadMobilePushNotifications ||
      defaultNewsfeedMobilePushNotifications
    ) {
      onChange({
        allowMobileAppPushNotifications: false,
        defaultDocumentUploadMobilePushNotifications: false,
        defaultNewsfeedMobilePushNotifications: false,
      });
    }
  }, [
    displayMobileAppPushNotificationsSection,
    hasDocumentVaultAccess,
    isNewsfeedEnabledOnOC,
    allowMobileAppPushNotifications,
    defaultDocumentUploadMobilePushNotifications,
    defaultNewsfeedMobilePushNotifications,
    onChange,
  ]);

  return (
    <>
      {hasOtherSettings && (
        <>
          <Separator />

          <section>
            <Text fontWeight={500} my="16">
              Other
            </Text>

            <Box pl="24">
              {showDataAggregation && (
                <Toggle
                  variant="setting"
                  checked={linkAccountsAccess}
                  onChange={evt => onChange({ linkAccountsAccess: evt.target.checked })}
                  my="16"
                >
                  Link Accounts
                </Toggle>
              )}

              {displayAddManualAccountToggle && (
                <Toggle
                  variant="setting"
                  checked={canAddManualAccount}
                  onChange={evt => onChange({ canAddManualAccount: evt.target.checked })}
                  my="16"
                >
                  Manual Accounts
                </Toggle>
              )}

              {showDataReconciliation && (
                <Toggle
                  variant="setting"
                  checked={dataReconciliationAccess}
                  onChange={evt => onChange({ dataReconciliationAccess: evt.target.checked })}
                  my="16"
                >
                  Reconciled Held-Away Accounts
                </Toggle>
              )}

              {showProBono && (
                <Toggle
                  variant="setting"
                  checked={isProBonoClient}
                  onChange={evt => onChange({ isProBonoClient: evt.target.checked })}
                  my="16"
                >
                  Pro Bono Client
                </Toggle>
              )}
            </Box>
          </section>
        </>
      )}

      {displayMobileAppPushNotificationsSection && (
        <>
          <Separator />

          <section>
            <Toggle
              variant="setting"
              checked={allowMobileAppPushNotifications}
              onChange={evt => handleMobileAppPushNotificationsChange(evt.target.checked)}
              my="16"
            >
              Mobile App Notifications
            </Toggle>

            <Text fontSize="small" color="neutral.500" mt={-12} mb="16">
              These settings will only affect clients using the non-branded Orion Wealth Management
              Portal mobile app.
            </Text>

            {allowMobileAppPushNotifications && (
              <Box pl="24">
                {hasDocumentVaultFirm && (
                  <Toggle
                    variant="setting"
                    checked={defaultDocumentUploadMobilePushNotifications}
                    disabled={!hasDocumentVaultAccess}
                    onChange={evt =>
                      onChange({
                        defaultDocumentUploadMobilePushNotifications: evt.target.checked,
                      })
                    }
                    my="16"
                  >
                    Document Upload
                  </Toggle>
                )}
                {isNewsfeedEnabledOnOC && (
                  <Toggle
                    variant="setting"
                    checked={defaultNewsfeedMobilePushNotifications}
                    onChange={evt =>
                      onChange({
                        defaultNewsfeedMobilePushNotifications: evt.target.checked,
                      })
                    }
                    my="16"
                  >
                    Newsfeed
                  </Toggle>
                )}
              </Box>
            )}
          </section>
        </>
      )}
    </>
  );
};
